import React from 'react';
import { Helmet } from 'react-helmet';

const EverydayLifeCalculators = () => {
  return (
    <div className="min-h-screen bg-gradient-to-r from-cyan-100 to-blue-100">
      <Helmet>
        <title>Everyday Life Calculators | Practical Tools for Daily Life</title>
        <meta name="description" content="Collection of 250+ everyday life calculators for solving common daily problems, financial planning, health management, and household calculations." />
        <meta name="keywords" content="everyday calculators, daily life tools, financial calculators, health calculators, household calculators, unit converters" />
        <meta property="og:title" content="Everyday Life Calculators | Practical Tools for Daily Life" />
        <meta property="og:description" content="Collection of 250+ everyday life calculators for solving common daily problems, financial planning, health management, and household calculations." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Everyday Life Calculators | Practical Tools for Daily Life" />
        <meta name="twitter:description" content="Collection of 250+ everyday life calculators for solving common daily problems, financial planning, health management, and household calculations." />
        <link rel="canonical" href="https://yourdomain.com/everyday-life-calculators" />
      </Helmet>

      {/* WhatsApp Support Button */}
      <div className="fixed bottom-6 right-6">
        <a 
          href="https://api.whatsapp.com/send?phone=YOUR_PHONE_NUMBER&text=I%20need%20help%20with%20the%20Everyday%20Life%20Calculators" 
          target="_blank" 
          rel="noopener noreferrer"
          className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-full shadow-lg transition-all transform hover:scale-105"
        >
          <span className="text-2xl">💬</span>
          <span className="sr-only">Contact us on WhatsApp</span>
        </a>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-green-600 mb-4">🧮</div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Everyday Life Calculators</h1>
          <p className="calculator-count text-xl text-gray-600">250+ calculators</p>
        </div>

        <div className="max-w-2xl mx-auto mb-12">
          <div className="calculator-description bg-white p-6 rounded-lg shadow-md">
            <p className="text-gray-600 mb-4">
              Simplify your daily life with our comprehensive collection of everyday calculators. From financial planning to health management, cooking conversions to travel planning, we've got tools to help with all your daily calculations.
            </p>
            <button className="read-more inline-block bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors">
              Read more
            </button>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Financial Calculators <span className="emoji ml-2">💰</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/budget" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Budget Calculator</h3>
                <p className="text-gray-600">Plan and track your monthly budget</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/loan" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Loan Calculator</h3>
                <p className="text-gray-600">Calculate loan payments and interest</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/savings" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Savings Calculator</h3>
                <p className="text-gray-600">Estimate your savings growth</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/mortgage" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Mortgage Calculator</h3>
                <p className="text-gray-600">Calculate mortgage payments</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/tip" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Tip Calculator</h3>
                <p className="text-gray-600">Calculate appropriate tips</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/vat" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">VAT Calculator</h3>
                <p className="text-gray-600">Calculate value-added tax</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Health & Wellness <span className="emoji ml-2">🍎</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/bmi" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">BMI Calculator</h3>
                <p className="text-gray-600">Calculate your Body Mass Index</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/calorie" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Calorie Calculator</h3>
                <p className="text-gray-600">Estimate daily calorie needs</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/water-intake" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Water Intake Calculator</h3>
                <p className="text-gray-600">Determine your daily water needs</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/blood-alcohol" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Blood Alcohol Calculator</h3>
                <p className="text-gray-600">Estimate blood alcohol concentration</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/pregnancy-due-date" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Pregnancy Due Date Calculator</h3>
                <p className="text-gray-600">Estimate your due date</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/retirement" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Retirement Calculator</h3>
                <p className="text-gray-600">Plan for your retirement needs</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Cooking & Food <span className="emoji ml-2">🍳</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/cooking-time" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Cooking Time Calculator</h3>
                <p className="text-gray-600">Determine cooking times for various foods</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/recipe-scaling" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Recipe Scaling Calculator</h3>
                <p className="text-gray-600">Adjust recipes for different serving sizes</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/food-cost" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Food Cost Calculator</h3>
                <p className="text-gray-600">Calculate the cost of recipes and meals</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/oven-temperature" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Oven Temperature Converter</h3>
                <p className="text-gray-600">Convert between °C, °F, and gas marks</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/portion-control" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Portion Control Calculator</h3>
                <p className="text-gray-600">Determine appropriate serving sizes</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/food-storage" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Food Storage Calculator</h3>
                <p className="text-gray-600">Determine how long food lasts</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Travel & Transportation <span className="emoji ml-2">🚗</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/fuel-cost" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Fuel Cost Calculator</h3>
                <p className="text-gray-600">Estimate fuel costs for trips</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/travel-time" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Travel Time Calculator</h3>
                <p className="text-gray-600">Calculate estimated travel times</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/currency-converter" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Currency Converter</h3>
                <p className="text-gray-600">Convert between different currencies</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/timezone-converter" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Time Zone Converter</h3>
                <p className="text-gray-600">Find current time in different time zones</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/luggage-weight" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Luggage Weight Calculator</h3>
                <p className="text-gray-600">Calculate luggage weight and avoid overcharges</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/flight-carbon" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Flight Carbon Calculator</h3>
                <p className="text-gray-600">Estimate your flight's carbon footprint</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Home & Garden <span className="emoji ml-2">🏡</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/paint-calculator" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Paint Calculator</h3>
                <p className="text-gray-600">Determine how much paint you need</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/flooring-calculator" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Flooring Calculator</h3>
                <p className="text-gray-600">Calculate materials needed for flooring</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/grass-seed-calculator" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Grass Seed Calculator</h3>
                <p className="text-gray-600">Determine how much seed you need</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/compost-calculator" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Compost Calculator</h3>
                <p className="text-gray-600">Calculate compost needs for your garden</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/yard-waste" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Yard Waste Calculator</h3>
                <p className="text-gray-600">Estimate yard waste volume</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/home-energy" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Home Energy Calculator</h3>
                <p className="text-gray-600">Estimate your home energy usage</p>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EverydayLifeCalculators;